/* eslint-disable no-prototype-builtins */

import PropTypes from 'prop-types';
import React from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import {
  OlsConfigStore,
  RouteHandler,
  ScrollWidgetActions,
  ShopViewConstants
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import dataAids from '../../../constants/dataAids';
import Fetching from '../Fetching';
import ProductGroupItem from '../ProductGroupItem';
import ViewAllLink from '../ViewAllLink';
import ErrorMessage from '../ErrorMessage';
import { ProductStarRating } from '../Yotpo';

const YotpoCSS = ({ products }) => {
  const { app_key: appKey } = OlsConfigStore.getState().yotpo_config || {};
  if (!appKey || !products || !products.length) {
    return null;
  }

  return (
    <style dangerouslySetInnerHTML={{
      __html: `
      .ratings-review-wrapper.product-list .yotpo-bottomline a {
        display: none;
      }
    ` }} />
  );
};

YotpoCSS.propTypes = {
  products: PropTypes.array
};

function hideEmptyStars() {
  const selector = '.ratings-review-wrapper.product-list .write-review-btn-hidden';
  document.querySelectorAll(selector).forEach(reviewElement => {
    // NEMO-8659: hide empty stars on Product List page
    reviewElement.parentNode.style.display = 'none';
  });
}

class ClassicLayout extends React.Component {
  constructor() {
    super(...arguments);
    this.handleClickViewAll = this.handleClickViewAll.bind(this);
  }

  componentDidUpdate() {
    /* eslint-disable no-undef */
    if (typeof yotpo !== 'undefined') {
      setTimeout(() => {
        const checkMethodName = method => method.name === hideEmptyStars.name;
        const yotpoCallbacks = yotpo.allWidgetsReadyCallback;
        const shouldAddCallback = yotpoCallbacks && yotpoCallbacks.filter(checkMethodName).length === 0;
        if (shouldAddCallback) {
          yotpo.allWidgetsReadyCallback.push(hideEmptyStars);
        }
        yotpo.initWidgets();
      }, 0);
    }
    /* eslint-enable no-undef */
  }

  handleClickViewAll(e) {
    e.preventDefault();

    RouteHandler.navigate(ShopViewConstants.PRODUCT_LISTING);
    ScrollWidgetActions.scrollShopWidget();
  }

  get showError() {
    const { willUseSampleData, useStaticData, error, configError, provisionHasError } = this.props;
    if (willUseSampleData || useStaticData) return false;
    if (provisionHasError) return true;

    return (configError && configError.hasOwnProperty('status')) || (error && error.hasOwnProperty('status'));
  }

  renderProductData(translate) {
    const {
      products,
      renderPrices,
      renderMode,
      shopWidgetSamePage,
      shopWidgetPageId,
      shopWidgetRoute,
      productDataWasLoaded,
      willUseSampleData,
      imageCropMethod
    } = this.props;
    const {
      Element: { Block, Text },
      Component: { Grid, Grid: { Cell } }
    } = UX2;
    const ratingsContainerStyles = {
      marginTop: 'xsmall',
      textAlign: 'center'
    };

    let productData;
    if (this.showError) {
      productData = (
        <ErrorMessage message={ translate('products.error_fetching_product_data') } />
      );
    } else if (!willUseSampleData && !productDataWasLoaded) {
      return null;
    } else if (products && products.length) {
      const colSize = products.length === 4 ? '6' : '4';
      const productItem = products.map(product => {
        return (
          <Cell key={ product.slug } xs={ 12 } sm={ colSize } style={{ marginBottom: 'large' }}>
            <ProductGroupItem
              productItemAs={ UX2.Component.CommerceCardItem }
              product={ product }
              renderPrices={ renderPrices }
              renderMode={ renderMode }
              shopWidgetSamePage={ shopWidgetSamePage }
              shopWidgetPageId={ shopWidgetPageId }
              shopWidgetRoute={ shopWidgetRoute }
              imageCropMethod={ imageCropMethod }
              ratings={
                <ProductStarRating
                  product={ product }
                  containerStyle={ ratingsContainerStyles }
                  renderMode={ renderMode }
                />
              }
            />
          </Cell>
        );
      });

      if (products.length === 4) {
        productData = (
          <Grid size={ 12 } style={{ justifyContent: 'center' }}>
            <Cell xs={ 12 } sm={ 8 }>
              <Grid children={ productItem } size={ 12 }/>
            </Cell>
          </Grid>
        );
      } else {
        productData = (
          <Grid size={ 12 } children={ productItem } style={{ justifyContent: 'center' }}/>
        );
      }
    } else {
      const noProductStyles = {
        paddingTop: 'medium',
        textAlign: 'center'
      };
      productData = (
        <Block style={ noProductStyles }>
          <Text
            children={ translate('featured_products.new_products_coming_soon') }
            data-aid={ dataAids.PG_NO_PRODUCTS_MESSAGE_RENDERED }
          />
        </Block>
      );
    }
    return productData;
  }

  renderBottomLinks(translate) {
    const {
      products,
      renderMode,
      shopWidgetSamePage,
      shopWidgetPageId,
      shopWidgetRoute
    } = this.props;

    if (shopWidgetPageId && products.length > 0) {
      const dataAid = dataAids.PRODUCT_GROUP_VIEW_ALL;

      const baseProps = {
        'href': `${shopWidgetRoute}/ols/products`,
        'onClick': this.handleClickViewAll,
        'data-page-query': 'olsPage=products',
        'data-page': shopWidgetPageId
      };

      return (
        <ViewAllLink
          baseProps={ baseProps }
          shopWidgetSamePage={ shopWidgetSamePage }
          renderMode={ renderMode }
          translate={ translate }
          data-aid={ dataAid } />
      );
    }
    return null;
  }

  render() {
    const translate = this.context.translate || (() => {});
    const { fetching, products, willUseSampleData } = this.props;
    const productData = this.renderProductData(translate);
    const bottomLinks = this.renderBottomLinks(translate);
    const { Block } = UX2.Element;

    return (
      <Block>
        <YotpoCSS products={ products } />
        <Block data-route=''>
          {productData}
          {bottomLinks}
        </Block>
        <Fetching fetching={ fetching } willUseSampleData={ willUseSampleData } />
      </Block>
    );
  }
}

ClassicLayout.propTypes = {
  products: PropTypes.array,
  imageCropMethod: PropTypes.string,
  error: PropTypes.object,
  configError: PropTypes.object,
  renderPrices: PropTypes.any,
  renderMode: PropTypes.string,
  shopWidgetSamePage: PropTypes.bool,
  shopWidgetPageId: PropTypes.any,
  shopWidgetRoute: PropTypes.any,
  productDataWasLoaded: PropTypes.bool,
  fetching: PropTypes.bool,
  willUseSampleData: PropTypes.bool,
  useStaticData: PropTypes.bool,
  provisionHasError: PropTypes.bool
};

ClassicLayout.defaultProps = {
  products: [],
  configError: null,
  willUseSampleData: false,
  provisionHasError: false,
  useStaticData: false
};

ClassicLayout.contextTypes = {
  translate: PropTypes.func,
  getLinkProps: PropTypes.func
};

ClassicLayout.childContextTypes = {
  imageCropMethod: PropTypes.string
};

export default ClassicLayout;
